import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useNavigate, Outlet, useLocation } from "react-router-dom";
import { Tooltip, Popover } from "antd";
import mailClientAPI from "../../api/mail-client-api";
import { useUser } from "../../context/UserContext";
import { Sidebar } from "./Sidebar";
import { ComposeModal } from "./ComposeModal";

function MailLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { userInfo } = useUser();
  const [mailboxes, setMailboxes] = useState([]);
  const [labels, setLabels] = useState([]);
  const [labelsLoading, setLabelsLoading] = useState(true);
  const [currentFolder, setCurrentFolder] = useState("inbox");
  const [isComposeOpen, setIsComposeOpen] = useState(false);
  const [draftId, setDraftId] = useState(null);
  const [draftMailbox, setDraftMailbox] = useState(null);
  const [draftEmailId, setDraftEmailId] = useState(null);
  const [draftUpdateCallbacks, setDraftUpdateCallbacks] = useState({ onDraftSaved: null, onDraftDeleted: null });
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [isProfilePopoverOpen, setIsProfilePopoverOpen] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  // Memoize registerDraftCallbacks to prevent infinite loops
  const registerDraftCallbacks = useCallback((callbacks) => {
    // Only update if callbacks actually changed to prevent infinite loops
    setDraftUpdateCallbacks(prev => {
      // Check if callbacks are actually different
      if (prev.onDraftSaved === callbacks.onDraftSaved && 
          prev.onDraftDeleted === callbacks.onDraftDeleted) {
        return prev; // No change, return previous state
      }
      return callbacks; // Callbacks changed, update state
    });
  }, []);

  // Map folder IDs to mailbox paths
  const getMailboxPath = (folderId) => {
    const folderMap = {
      inbox: "INBOX",
      sent: "Sent",
      drafts: "Drafts",
      scheduled: "Scheduled",
      archived: "Archive",
      spam: "Junk",
      trash: "Trash",
    };
    return folderMap[folderId] || "INBOX";
  };

  // Map mailbox paths to folder IDs
  const getFolderId = (mailboxPath) => {
    if (!mailboxPath || typeof mailboxPath !== 'string') {
      return "inbox"; // Default fallback
    }
    const path = mailboxPath.toLowerCase();
    if (path === "inbox") return "inbox";
    if (path.includes("sent")) return "sent";
    if (path.includes("draft")) return "drafts";
    if (path === "scheduled") return "scheduled";
    if (path.includes("archive")) return "archived";
    if (path.includes("junk") || path.includes("spam")) return "spam";
    if (path.includes("trash")) return "trash";
    return "inbox";
  };

  // Load mailboxes
  useEffect(() => {
    const loadMailboxes = async () => {
      setLoading(true);
      try {
        const response = await mailClientAPI.getMailboxes();
        setMailboxes(response.Output?.mailboxes || []);
      } catch (error) {
        console.error("Error loading mailboxes:", error);
      } finally {
        setLoading(false);
      }
    };
    loadMailboxes();
  }, [userInfo?.email]);

  // Load labels
  useEffect(() => {
    const loadLabels = async () => {
      setLabelsLoading(true);
      try {
        const response = await mailClientAPI.getLabels();
        setLabels(response.Output?.labels || []);
      } catch (error) {
        console.error("Error loading labels:", error);
        setLabels([]);
      } finally {
        setLabelsLoading(false);
      }
    };
    loadLabels();
  }, [userInfo?.email]);

  // Function to refresh labels (can be called after create/update/delete)
  const refreshLabels = async () => {
    try {
      const response = await mailClientAPI.getLabels();
      setLabels(response.Output?.labels || []);
    } catch (error) {
      console.error("Error refreshing labels:", error);
    }
  };

  // Update current folder and search query based on route
  useEffect(() => {
    const path = location.pathname;
    const searchParams = new URLSearchParams(location.search);
    
    if (path.startsWith("/mailboxes/")) {
      const pathParts = path.replace("/mailboxes/", "").split("/");
      const mailboxPath = decodeURIComponent(pathParts[0]);
      const folderId = getFolderId(mailboxPath);
      setCurrentFolder(folderId);
      
      // Update search query from URL if it's a search route
      if (pathParts[1] === "search") {
        const q = searchParams.get("q") || "";
        setSearchQuery(q);
      } else {
        // Clear search if not on search route
        setSearchQuery("");
      }
    } else if (path === "" || path === "/") {
      setCurrentFolder("inbox");
      setSearchQuery("");
      // Redirect to inbox mailbox
      navigate("/mailboxes/INBOX", { replace: true });
    }
  }, [location.pathname, location.search, navigate]);

  // Calculate unread counts
  const unreadCounts = useMemo(() => {
    const counts = {};
    mailboxes.forEach((mailbox) => {
      const folderId = getFolderId(mailbox.path);
      counts[folderId] = (counts[folderId] || 0) + (mailbox.unreadCount || 0);
    });
    return counts;
  }, [mailboxes]);

  // Calculate total counts
  const totalCounts = useMemo(() => {
    const counts = {};
    mailboxes.forEach((mailbox) => {
      const folderId = getFolderId(mailbox.path);
      counts[folderId] = (counts[folderId] || 0) + (mailbox.messages || 0);
    });
    return counts;
  }, [mailboxes]);

  // Function to update mailbox counts when actions are performed
  const updateMailboxCounts = useCallback((updates) => {
    // updates: { sourceFolder?, destinationFolder?, wasUnread, count }
    // This function updates the mailboxes state to reflect count changes
    if (!updates || typeof updates !== 'object') {
      return; // Invalid updates object
    }
    
    setMailboxes((prevMailboxes) => {
      return prevMailboxes.map((mailbox) => {
        if (!mailbox || !mailbox.path) {
          return mailbox; // Skip invalid mailboxes
        }
        const sourceFolderId = updates.sourceFolder ? getFolderId(updates.sourceFolder) : null;
        const destinationFolderId = updates.destinationFolder ? getFolderId(updates.destinationFolder) : null;
        const mailboxFolderId = getFolderId(mailbox.path);
        
        const updated = { ...mailbox };
        let changed = false;

        // If this mailbox is the source folder, decrease counts
        if (sourceFolderId && mailboxFolderId === sourceFolderId) {
          // Decrease total count
          updated.messages = Math.max(0, (updated.messages || 0) - updates.count);
          // Decrease unread count if email was unread
          if (updates.wasUnread) {
            updated.unreadCount = Math.max(0, (updated.unreadCount || 0) - updates.count);
          }
          changed = true;
        }

        // If this mailbox is the destination folder, increase counts
        if (destinationFolderId && mailboxFolderId === destinationFolderId) {
          // Increase total count
          updated.messages = (updated.messages || 0) + updates.count;
          // Increase unread count if email was unread
          if (updates.wasUnread) {
            updated.unreadCount = (updated.unreadCount || 0) + updates.count;
          }
          changed = true;
        }

        // If just changing read status (no move), update unread count
        if (updates.readStatusChange && sourceFolderId && mailboxFolderId === sourceFolderId) {
          if (updates.readStatusChange === 'read') {
            // Marking as read: decrease unread count
            updated.unreadCount = Math.max(0, (updated.unreadCount || 0) - updates.count);
            changed = true;
          } else if (updates.readStatusChange === 'unread') {
            // Marking as unread: increase unread count
            updated.unreadCount = (updated.unreadCount || 0) + updates.count;
            changed = true;
          }
        }

        return changed ? updated : mailbox;
      });
    });
  }, []);

  const handleLogout = async () => {
    setIsProfilePopoverOpen(false);
    await mailClientAPI.logout();
    navigate("/login");
  };

  const handleMyAccount = () => {
    setIsProfilePopoverOpen(false);
    navigate("/settings");
  };

  const handleSwitchOrganization = () => {
    setIsProfilePopoverOpen(false);
    const mainAppUrl = process.env.REACT_APP_GRIGORA_MAIN_APP_URL;
    if (mainAppUrl) {
      window.open(`${mainAppUrl}/emails`, '_blank');
    }
  };

  const handleVisitWebsite = () => {
    setIsProfilePopoverOpen(false);
    // Extract domain from email (e.g., user@example.com -> example.com)
    if (userInfo?.email) {
      const domain = userInfo.email.split('@')[1];
      if (domain) {
        window.open(`https://${domain}`, '_blank');
      }
    }
  };

  const handleHelpSupport = () => {
    setIsProfilePopoverOpen(false);
    window.open('https://help.grigora.co', '_blank');
  };

  const handleSelectFolder = (folderId) => {
    console.log("folderId", folderId);
    const mailboxPath = getMailboxPath(folderId);
    navigate(`/mailboxes/${encodeURIComponent(mailboxPath)}`);
  };

  const getUserDisplayName = () => {
    // Try to get name from userInfo, or use email prefix
    if (userInfo?.name) {
      return userInfo.name;
    }
    if (userInfo?.email) {
      const emailPrefix = userInfo.email.split("@")[0];
      return emailPrefix.charAt(0).toUpperCase() + emailPrefix.slice(1);
    }
    return "User";
  };

  return (
    <div className="flex h-screen w-full bg-white overflow-hidden text-gray-800 font-sans">
      {/* Sidebar */}
      <Sidebar
        isOpen={isSidebarOpen}
        onClose={() => setIsSidebarOpen(false)}
        currentFolder={currentFolder}
        onSelectFolder={(folderId) => {
          handleSelectFolder(folderId);
          setIsSidebarOpen(false); // Close drawer after selection on mobile
        }}
        onCompose={() => {
          setIsComposeOpen(true);
          setIsSidebarOpen(false); // Close drawer when composing
        }}
        unreadCounts={unreadCounts}
        totalCounts={totalCounts}
        labels={labels}
        labelsLoading={labelsLoading}
        refreshLabels={refreshLabels}
      />

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top Header / Search */}
        <header className="h-16 border-b border-gray-200 flex items-center px-4 bg-white justify-between shrink-0">
          {/* Menu Button - Visible on mobile, hidden on desktop */}
          <button
            onClick={() => setIsSidebarOpen(true)}
            className="md:hidden p-2 -ml-2 hover:bg-gray-100 rounded-full text-gray-600 mr-2"
            aria-label="Open menu"
          >
            <span className="material-icons-outlined text-2xl">menu</span>
          </button>
          <div className="flex-1 max-w-2xl">
            <div className="relative group">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <span className="material-icons-outlined text-gray-400">search</span>
              </div>
              <input
                type="text"
                className="block w-full pl-10 pr-3 py-2.5 border-none leading-5 bg-gray-100 text-gray-900 placeholder-gray-500 focus:outline-none focus:bg-white focus:ring-2 focus:ring-grigora-100 focus:shadow-sm rounded-lg sm:text-sm transition-all"
                placeholder={`Search in ${currentFolder}...`}
                value={searchQuery}
                onChange={(e) => {
                  // Only update the input value, don't search
                  setSearchQuery(e.target.value);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    const value = searchQuery.trim();
                    if (value) {
                      const mailboxPath = getMailboxPath(currentFolder);
                      const searchParams = new URLSearchParams({
                        q: value,
                        p: "1",
                        to: value,
                        from: value,
                        subject: value,
                      });
                      navigate(`/mailboxes/${encodeURIComponent(mailboxPath)}/search?${searchParams.toString()}`);
                    } else {
                      // Clear search - navigate back to regular mailbox view
                      const mailboxPath = getMailboxPath(currentFolder);
                      navigate(`/mailboxes/${encodeURIComponent(mailboxPath)}`);
                    }
                  }
                }}
              />
            </div>
          </div>

          <div className="ml-4 flex items-center space-x-3">
            {/* Settings Icon */}
            <button 
              className="flex p-2 text-gray-500 hover:bg-gray-100 rounded-full relative"
              title="Settings"
              onClick={() => navigate("/settings")}
            >
              <span className="material-icons-outlined text-2xl leading-none">settings</span>
            </button>
            
            {/* Profile Dropdown */}
            <Popover
              content={
                <div className="w-64">
                  <div className="px-4 py-3 border-b border-gray-50">
                    <p className="text-sm font-semibold text-gray-900">{getUserDisplayName()}</p>
                    <p className="text-xs text-gray-500 truncate">{userInfo?.email || "No email"}</p>
                  </div>
                  
                  <div className="py-1">
                    <button 
                      className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3 transition-colors"
                      onClick={handleMyAccount}
                    >
                      <span className="material-icons-outlined text-gray-400 text-lg">account_circle</span>
                      My Account
                    </button>
                    <button 
                      className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3 transition-colors"
                      onClick={handleSwitchOrganization}
                    >
                      <span className="material-icons-outlined text-gray-400 text-lg">domain</span>
                      Switch Organization
                    </button>
                  </div>

                  <div className="border-t border-gray-50 py-1">
                    <button 
                      className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3 transition-colors"
                      onClick={handleVisitWebsite}
                    >
                      <span className="material-icons-outlined text-gray-400 text-lg">language</span>
                      Visit Website
                    </button>
                    <button 
                      className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3 transition-colors"
                      onClick={handleHelpSupport}
                    >
                      <span className="material-icons-outlined text-gray-400 text-lg">help_outline</span>
                      Help & Support
                    </button>
                    <button 
                      className="w-full text-left px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 flex items-center gap-3 transition-colors" 
                      onClick={handleLogout}
                    >
                      <span className="material-icons-outlined text-red-400 text-lg">logout</span>
                      Logout
                    </button>
                  </div>
                </div>
              }
              trigger="click"
              placement="bottomRight"
              overlayClassName="profile-popover"
              open={isProfilePopoverOpen}
              onOpenChange={setIsProfilePopoverOpen}
            >
              <div className="relative ml-2">
                <div className="h-8 w-8 rounded-full bg-grigora-100 text-grigora-600 flex items-center justify-center font-bold border border-grigora-200 cursor-pointer hover:shadow-md transition-shadow select-none">
                  {getUserDisplayName().charAt(0).toUpperCase()}
                </div>
              </div>
            </Popover>
          </div>
        </header>

        {/* Outlet for nested routes */}
        <main className="flex-1 overflow-hidden relative flex flex-col">
          <Outlet context={{ 
            mailboxes, 
            loading, 
            searchQuery, 
            currentFolder,
            labels,
            labelsLoading,
            refreshLabels,
            updateMailboxCounts,
            openCompose: () => setIsComposeOpen(true),
            openDraft: (draftId, mailbox, emailId) => {
              setDraftId(draftId);
              setDraftMailbox(mailbox);
              setDraftEmailId(emailId);
              setIsComposeOpen(true);
            },
            registerDraftCallbacks: registerDraftCallbacks,
          }} />
        </main>
      </div>

      {/* Compose Modal */}
      <ComposeModal 
        isOpen={isComposeOpen} 
        onClose={() => {
          setIsComposeOpen(false);
          setDraftId(null);
          setDraftMailbox(null);
          setDraftEmailId(null);
        }}
        draftId={draftId}
        draftMailbox={draftMailbox}
        draftEmailId={draftEmailId}
        updateMailboxCounts={updateMailboxCounts}
        onDraftSaved={(draftInfo, oldDraftId = null) => {
          // Call the registered callback from MailboxView
          if (draftUpdateCallbacks.onDraftSaved) {
            draftUpdateCallbacks.onDraftSaved(draftInfo, oldDraftId);
          }
        }}
        onDraftDeleted={(mailbox, emailId) => {
          // Call the registered callback from MailboxView
          if (draftUpdateCallbacks.onDraftDeleted) {
            draftUpdateCallbacks.onDraftDeleted(mailbox, emailId);
          }
        }}
      />
    </div>
  );
}

export default MailLayout;

